import React, { useEffect, useState } from "react";
import { Check, Sparkles, X } from "lucide-react";
import { api } from "../lib/api";

// Portrait engine picker (two cards, price in the corner, "Baru" badge) + reference-photo rules with the Support Agent portrait as the good example.
export function usePortraitEngines() {
  const [engines, setEngines] = useState(null);
  useEffect(() => { api.get("/personas/portrait-engines").then((r) => setEngines(r.data.items)).catch(() => setEngines([{ id: "standard", label: "Potret Standar", credits: null }])); }, []);
  return engines;
}

export function PortraitEngineCards({ engines, value, onChange, disabled }) {
  if (!engines || engines.length < 2) return null;  // without the second engine there is nothing to choose
  return (
    <div data-testid="portrait-engine-cards">
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Mesin potret</p>
      <div className="grid grid-cols-2 gap-2">
        {engines.map((e) => {
          const on = value === e.id;
          return (
            <button key={e.id} type="button" disabled={disabled} onClick={() => onChange(e.id)} data-testid={`portrait-engine-${e.id}`} aria-pressed={on}
              className={`relative rounded-2xl border p-3 text-left transition ${on ? "border-[#2F6BFF] bg-[#EEF3FF] shadow-sm" : "border-[#E7ECF3] bg-white hover:border-[#C9D6F2]"} disabled:opacity-60`}>
              <div className="mb-1 flex items-center gap-1.5">
                <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-bold text-white" data-testid={`portrait-engine-${e.id}-price`}>{e.credits} kredit</span>
                {e.new && <span className="rounded-md bg-[#2F6BFF] px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">Baru</span>}
              </div>
              <p className="flex items-center gap-1.5 text-sm font-bold text-slate-900">
                {e.id === "cinematic" && <Sparkles size={13} className="text-[#2F6BFF]" />}{e.label}
              </p>
              <p className="mt-1 text-[11px] leading-snug text-slate-500">{e.desc}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Style chips for the cinematic engine (presets come from the engines endpoint; null = engine default look).
export function PortraitPresetChips({ engines, engine, value, onChange, disabled }) {
  const presets = engines?.find((e) => e.id === engine)?.presets;
  if (!presets?.length) return null;
  const Chip = ({ id, label, desc }) => (
    <button type="button" disabled={disabled} onClick={() => onChange(id)} title={desc || ""} aria-pressed={value === id} data-testid={`portrait-preset-${id || "default"}`}
      className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${value === id ? "border-[#2F6BFF] bg-[#2F6BFF] text-white" : "border-[#E7ECF3] bg-white text-slate-600 hover:border-[#C9D6F2]"} disabled:opacity-60`}>
      {label}
    </button>
  );
  return (
    <div data-testid="portrait-preset-chips">
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Gaya potret</p>
      <div className="flex flex-wrap gap-1.5">
        <Chip id={null} label="Sinematik (bawaan)" />
        {presets.map((p) => <Chip key={p.id} {...p} />)}
      </div>
      {value && <p className="mt-1 text-[11px] text-slate-500" data-testid="portrait-preset-desc">{presets.find((p) => p.id === value)?.desc}</p>}
    </div>
  );
}

const GOOD = ["Satu wajah, menghadap kamera", "Pencahayaan rata, latar polos", "Bahu ke atas, wajah terlihat jelas", "Tanpa kacamata hitam atau masker"];
const BAD = ["Wajah tokoh publik / orang lain tanpa izin", "Anak-anak atau konten tidak pantas", "Teks, logo, atau watermark di foto", "Tangkapan layar buram atau ramai"];

export function ReferencePhotoRules({ compact = false }) {
  const [example, setExample] = useState(null);
  useEffect(() => { api.get("/support/agents").then((r) => setExample(r.data?.[0]?.portrait || r.data?.items?.[0]?.portrait || "/brand/oryntix-support.jpg")).catch(() => setExample("/brand/oryntix-support.jpg")); }, []);
  return (
    <div className="rounded-2xl border border-[#E7ECF3] bg-white p-3" data-testid="reference-photo-rules">
      <p className="text-xs font-semibold text-slate-800">Aturan foto referensi</p>
      <div className={`mt-2 flex gap-3 ${compact ? "flex-col" : "flex-col sm:flex-row"}`}>
        <div className="flex shrink-0 gap-2">
          <figure className="text-center">
            <img src={example || "/brand/oryntix-support.jpg"} alt="Contoh foto yang benar" className="h-20 w-20 rounded-xl object-cover ring-2 ring-emerald-500" data-testid="reference-photo-good-example" />
            <figcaption className="mt-1 flex items-center justify-center gap-0.5 text-[10px] font-semibold text-emerald-600"><Check size={11} /> Benar</figcaption>
          </figure>
          <figure className="text-center">
            <div aria-label="Contoh foto yang salah" className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-xl bg-[linear-gradient(135deg,#cbd5e1_0%,#94a3b8_40%,#e2e8f0_60%,#64748b_100%)] blur-[1px] ring-2 ring-[#EF4444]" data-testid="reference-photo-bad-example">
              <span className="rotate-[-12deg] text-[9px] font-black tracking-widest text-white/80">TEKS·LOGO</span>
            </div>
            <figcaption className="mt-1 flex items-center justify-center gap-0.5 text-[10px] font-semibold text-[#EF4444]"><X size={11} /> Hindari</figcaption>
          </figure>
        </div>
        <div className={`grid flex-1 grid-cols-1 gap-x-3 gap-y-0.5 text-[11px] ${compact ? "" : "sm:grid-cols-2"}`}>
          {GOOD.map((t) => <p key={t} className="flex items-start gap-1 text-slate-600"><Check size={11} className="mt-0.5 shrink-0 text-emerald-500" />{t}</p>)}
          {BAD.map((t) => <p key={t} className="flex items-start gap-1 text-slate-600"><X size={11} className="mt-0.5 shrink-0 text-[#EF4444]" />{t}</p>)}
        </div>
      </div>
      <p className="mt-2 text-[10px] text-slate-400">Mesin dapat menolak foto yang tidak memenuhi syarat — kredit dikembalikan otomatis.</p>
    </div>
  );
}
